import type {
  ExperimentProgressEntry,
  ExperimentProgressProjection,
  ExperimentProgressStatus
} from "@roboscientist/contracts";
import { AlertTriangle } from "lucide-react";
import { OverflowReveal, Panel, StatusPill } from "@roboscientist/ui";
import { useId, useLayoutEffect, useRef, useState } from "react";
import {
  completedProgressColumns,
  progressStatusLabel,
  progressStatusTone
} from "./experimentProgressPresentation.js";

const statusOrder: ExperimentProgressStatus[] = [
  "running", "planned", "completed", "blocked", "cancelled"
];
const completedCardMinWidth = 280;
const progressCardGap = 7;

function ProgressEntryCard({ entry }: { entry: ExperimentProgressEntry }) {
  return <article className="experiment-progress-entry">
    <div className="experiment-progress-entry__heading">
      <strong>{entry.title}</strong>
      <StatusPill tone={progressStatusTone(entry.status)}>{entry.status}</StatusPill>
    </div>
    <p>{entry.description}</p>
    {entry.result ? <div className="experiment-progress-result"><small>Result</small><span>{entry.result}</span></div> : null}
    {entry.metrics.length ? <div className="experiment-progress-metrics">
      {entry.metrics.map((metric, index) => <span key={`${metric.name}:${index}`}>
        <OverflowReveal as="small" value={metric.name} label="Metric name" /><strong>{metric.display}</strong>
      </span>)}
    </div> : null}
    {entry.references.length ? <div className="experiment-progress-references">
      {entry.references.slice(0, 3).map((reference) => <OverflowReveal as="code" key={reference} value={reference} kind={reference.includes("/") ? "path" : "identifier"} label="Progress reference" copyable focusable />)}
      {entry.references.length > 3 ? <small>+{entry.references.length - 3} references</small> : null}
    </div> : null}
  </article>;
}

function ProgressGroup({
  status,
  entries
}: {
  status: ExperimentProgressStatus;
  entries: ExperimentProgressEntry[];
}) {
  const [completedExpanded, setCompletedExpanded] = useState(false);
  const [completedColumns, setCompletedColumns] = useState(1);
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  useLayoutEffect(() => {
    if (status !== "completed") return;
    const element = listRef.current;
    if (!element) return;
    const updateColumns = () => {
      const width = element.getBoundingClientRect().width;
      if (width <= 0) return;
      setCompletedColumns(completedProgressColumns(
        width,
        completedCardMinWidth,
        progressCardGap
      ));
    };
    updateColumns();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateColumns);
    observer.observe(element);
    return () => observer.disconnect();
  }, [entries.length, status]);

  const completedCollapsible = status === "completed" && entries.length > completedColumns;
  const visibleEntries = completedCollapsible && !completedExpanded
    ? entries.slice(0, completedColumns)
    : entries;
  return <section className={`experiment-progress-group experiment-progress-group--${status}`}>
    <header>
      <div><span>{progressStatusLabel[status]}</span><small>{entries.length}</small></div>
      {completedCollapsible ? <button
        type="button"
        className="experiment-progress-group__toggle"
        aria-expanded={completedExpanded}
        aria-controls={listId}
        onClick={() => setCompletedExpanded((expanded) => !expanded)}
      >{completedExpanded ? "Collapse" : `Show all completed (${entries.length})`}</button> : null}
    </header>
    <div id={listId} ref={listRef} className="experiment-progress-list">
      {visibleEntries.map((entry) => <ProgressEntryCard key={entry.id} entry={entry} />)}
    </div>
  </section>;
}

export function ExperimentProgressPanel({
  progress,
  warning
}: {
  progress: ExperimentProgressProjection | null;
  warning: string | null;
}) {
  if (!progress && !warning) return null;
  if (!progress) return <Panel
    title="Experiment Progress"
    eyebrow="AGENT-MAINTAINED RESEARCH SNAPSHOT"
    className="experiment-progress-panel"
  >
    <div className="experiment-progress-warning"><AlertTriangle size={16} />{warning}</div>
  </Panel>;

  const counts = new Map(statusOrder.map((status) => [
    status,
    progress.progress.filter((entry) => entry.status === status).length
  ]));
  return <Panel
    title="Experiment Progress"
    eyebrow="AGENT-MAINTAINED · INCREMENTAL RESEARCH SNAPSHOT"
    className="experiment-progress-panel"
    action={<time className="experiment-progress-updated" dateTime={progress.updatedAt}>
      Updated {new Date(progress.updatedAt).toLocaleString()}
    </time>}
  >
    {warning ? <div className="experiment-progress-warning"><AlertTriangle size={16} />{warning}</div> : null}
    <div className="experiment-progress-overview">
      <div><strong>{progress.title}</strong><p>{progress.summary}</p></div>
      <div className="experiment-progress-counts" aria-label="Experiment progress counts">
        {statusOrder.slice(0, 4).map((status) => <span key={status}>
          <strong>{counts.get(status) ?? 0}</strong><small>{progressStatusLabel[status]}</small>
        </span>)}
      </div>
    </div>
    <div className="experiment-progress-groups">
      {statusOrder.map((status) => {
        const entries = progress.progress.filter((entry) => entry.status === status);
        if (!entries.length) return null;
        return <ProgressGroup
          key={`${progress.experimentId}:${status}`}
          status={status}
          entries={entries}
        />;
      })}
    </div>
  </Panel>;
}
